import {
  ResourceStatus,
  ViewableApplicationResource,
} from 'src/app/account/components/app-resources-view/app-resources-view.component'
import { ResourcesTab } from 'src/app/account/components/application/resources-tab'
import {
  FailbackStatus,
  RecoveryEnvironmentStatus,
  STATUS_TEXT,
} from 'src/app/account/components/application/statusText'
import { AppStatusState } from 'src/app/account/components/process-state/process-state.component'
import { Application } from 'src/app/account/services/application.service'
import { ApplicationType, SyncPhaseDto } from 'src/app/services/dtos'
import { ResourceType } from 'src/utils/resource-types'
import {
  ACTION_DISABLED_AZURE_REGION_PAIR,
  ACTION_DISABLED_CONCLUDING_TO_STANDBY,
  ACTION_DISABLED_FAILBACK_IN_PROGRESS,
  ACTION_DISABLED_RESTORE_IN_PROGRESS,
  ACTION_DISABLED_STANDBY_REFRESH,
  MockApplicationOptions,
  RecoveryScreenTabBarFlags,
  buildMockApplication,
  mkRecoveryResource,
  tabBarFailover,
  tabBarFailoverTest,
  tabBarStandby,
} from './showcase-mock-core'

export interface RecoveryScreenOperationNotice {
  recoveryPointTimeString: string
  showNetworkSandboxStatus: boolean
  networkSandboxStatus: 'enabled' | 'disabled'
  // Drives the "See most recent events" inline link in advanced sandbox mode.
  showSandboxEventsLink: boolean
  showRansomwareRecoveryStatus: boolean
  ransomwareRecoveryStatus: 'enabled' | 'disabled'
  // Empty string = no row (mirrors the *ngIf gate in application.component.html).
  domainAllowlistStatus: string
  whitelistStatus: string
}

export interface RecoveryScreenScenario {
  key: string
  label: string
  description: string
  // Which sub-tab the scenario renders under. Drives the Recovery/Failback
  // chip-button highlight and `tabType` fed into app-resources-view.
  tab: 'recovery' | 'failback'
  // optgroup label in the dropdown — phase or context bucket.
  group: 'STANDBY' | 'FAILOVER' | 'FAILOVER_TEST' | 'CLEANUP' | 'FAILBACK'
  syncPhase: SyncPhaseDto
  application: Application
  resources: ViewableApplicationResource[]
  // RecoveryEnvironmentStatus | FailbackStatus depending on tab. Tab-status-bar
  // template consumes both shapes interchangeably.
  status: RecoveryEnvironmentStatus | FailbackStatus
  // null = render no notice (e.g. STANDBY recovery — matches the
  // `*ngIf="activeTab === Tab.RECOVERY_ENVIRONMENT && !isStandby"` gate).
  // For Failback, null mirrors `failbackOperationNoticeText` returning null.
  operationNotice: RecoveryScreenOperationNotice | null
  failbackOperationNoticeText: string | null
  tabBarFlags: RecoveryScreenTabBarFlags
}

// EKS parent + child pods. Children carry `parentArn` pointing at the
// cluster ARN so app-resources-view's buildDisplayRows groups them under
// the parent. Forcing one child into FAILED is what trips the
// needs-attention rollup pill on the cluster row.
function mkEksParentWithChildren(args: {
  clusterName: string
  childStatuses: ResourceStatus[]
}): ViewableApplicationResource[] {
  const parentArn = `arn:aws:eks:us-east-1:123456789012:cluster/${args.clusterName}`
  const parent = mkRecoveryResource({
    name: args.clusterName,
    type: ResourceType.eksCluster,
    status: ResourceStatus.SUCCEEDED,
    arn: parentArn,
  })
  const children = args.childStatuses.map((s, i) =>
    mkRecoveryResource({
      name: `${args.clusterName}-deploy-${i + 1}`,
      type: ResourceType.k8sResource,
      status: s,
      parentArn,
      arn: `${parentArn}/deployment-${i + 1}`,
    })
  )
  return [parent, ...children]
}

// Convenience: render the standard rendered "recovery point at HH:MM on Mon DD"
// suffix the real status strings use — keeps scenario labels consistent with
// production wording.
const RP_AT_3_14_MAY_8 = ' from recovery point at 03:14 PM on May 8'

// Hand-tuned resource sets per scenario. Realistic resource type mixes so
// the row count and filter pills look right (e.g., RDS + EC2 + S3 instead of
// the round-robin enum loop in buildStatusResources()).
function resourcesEmpty(): ViewableApplicationResource[] {
  return []
}

function resourcesAllPending(): ViewableApplicationResource[] {
  return [
    mkRecoveryResource({
      name: 'web-app-01',
      type: ResourceType.ec2Instance,
      status: ResourceStatus.PROCESSING,
      progress: 0.4,
    }),
    mkRecoveryResource({ name: 'db-primary', type: ResourceType.rdsDbInstance, status: ResourceStatus.PENDING }),
    mkRecoveryResource({ name: 'app-data', type: ResourceType.s3Bucket, status: ResourceStatus.PENDING }),
    // PENDING with the post-job-lag substatus — covers the "Confirming
    // status..." path that _computeStatus emits during the 30s window.
    mkRecoveryResource({
      name: 'reporting-fn',
      type: ResourceType.lambdaFunction,
      status: ResourceStatus.PENDING,
      substatus: 'Confirming status...',
    }),
    mkRecoveryResource({
      name: 'orders',
      type: ResourceType.dynamoDbTable,
      status: ResourceStatus.PROCESSING,
      progress: 0.8,
    }),
  ]
}

function resourcesSteadyState(): ViewableApplicationResource[] {
  return [
    mkRecoveryResource({ name: 'web-app-01', type: ResourceType.ec2Instance, status: ResourceStatus.SUCCEEDED }),
    mkRecoveryResource({ name: 'web-app-02', type: ResourceType.ec2Instance, status: ResourceStatus.SUCCEEDED }),
    mkRecoveryResource({ name: 'db-primary', type: ResourceType.rdsDbInstance, status: ResourceStatus.SUCCEEDED }),
    mkRecoveryResource({ name: 'app-data', type: ResourceType.s3Bucket, status: ResourceStatus.SUCCEEDED }),
    // Pilot light — source visible, target blank, status flipped to SUCCEEDED
    // at construction time. Substatus surfaces the hint via tooltip on the pill.
    mkRecoveryResource({
      name: 'jump-host',
      type: ResourceType.ec2Instance,
      status: ResourceStatus.SUCCEEDED,
      targetName: '',
    }),
    // Orphaned target — target visible, source blank, NO_STATUS.
    mkRecoveryResource({
      name: 'old-test-bucket',
      type: ResourceType.s3Bucket,
      status: ResourceStatus.NO_STATUS,
      sourceName: '',
    }),
  ]
}

function resourcesMixedFailures(): ViewableApplicationResource[] {
  return [
    mkRecoveryResource({ name: 'web-app-01', type: ResourceType.ec2Instance, status: ResourceStatus.SUCCEEDED }),
    mkRecoveryResource({
      name: 'db-primary',
      type: ResourceType.rdsDbInstance,
      status: ResourceStatus.FAILED,
      message: 'DBSubnetGroup not found in target VPC.',
    }),
    mkRecoveryResource({
      name: 'db-replica',
      type: ResourceType.rdsDbInstance,
      status: ResourceStatus.NOT_PROCESSED,
    }),
    mkRecoveryResource({
      name: 'reporting-fn',
      type: ResourceType.lambdaFunction,
      status: ResourceStatus.NOT_PROCESSED,
    }),
    mkRecoveryResource({ name: 'app-data', type: ResourceType.s3Bucket, status: ResourceStatus.SUCCEEDED }),
  ]
}

function resourcesBlocked(): ViewableApplicationResource[] {
  return [
    mkRecoveryResource({ name: 'web-app-01', type: ResourceType.ec2Instance, status: ResourceStatus.SUCCEEDED }),
    mkRecoveryResource({
      name: 'web-app-02',
      type: ResourceType.ec2Instance,
      status: ResourceStatus.UNSATISFIED_DEPENDENCY,
      message: 'Waiting on db-primary',
    }),
    mkRecoveryResource({
      name: 'app-data',
      type: ResourceType.s3Bucket,
      status: ResourceStatus.UNSATISFIED_DEPENDENCY,
    }),
    mkRecoveryResource({
      name: 'db-primary',
      type: ResourceType.rdsDbInstance,
      status: ResourceStatus.PROCESSING,
      progress: 0.7,
    }),
  ]
}

function resourcesEksWithFailedChild(): ViewableApplicationResource[] {
  return [
    mkRecoveryResource({ name: 'web-app-01', type: ResourceType.ec2Instance, status: ResourceStatus.SUCCEEDED }),
    ...mkEksParentWithChildren({
      clusterName: 'orders-cluster',
      childStatuses: [
        ResourceStatus.SUCCEEDED,
        ResourceStatus.SUCCEEDED,
        ResourceStatus.PROCESSING,
        ResourceStatus.FAILED,
      ],
    }),
  ]
}

// Configured resources that have never been restored — all NO_STATUS, so
// successCount/inProgressCount/failedCount are all 0 while totalCount > 0.
// Drives the NEVER_ATTEMPTED "waiting / preparing" copy without a ratio.
function resourcesAllNoStatus(): ViewableApplicationResource[] {
  return [
    mkRecoveryResource({ name: 'web-app-01', type: ResourceType.ec2Instance, status: ResourceStatus.NO_STATUS }),
    mkRecoveryResource({ name: 'db-primary', type: ResourceType.rdsDbInstance, status: ResourceStatus.NO_STATUS }),
    mkRecoveryResource({ name: 'app-data', type: ResourceType.s3Bucket, status: ResourceStatus.NO_STATUS }),
  ]
}

// Job-level restore failure: one resource landed before the job died, the rest
// never processed. No FAILED rows (failedCount = 0) so the status falls through
// to the initialRestoreFailed / failedToApplyBackup / restoreFailed copy, with a
// partial ratio (1/4).
function resourcesPartialNoFailure(): ViewableApplicationResource[] {
  return [
    mkRecoveryResource({ name: 'web-app-01', type: ResourceType.ec2Instance, status: ResourceStatus.SUCCEEDED }),
    mkRecoveryResource({ name: 'db-primary', type: ResourceType.rdsDbInstance, status: ResourceStatus.NO_STATUS }),
    mkRecoveryResource({ name: 'app-data', type: ResourceType.s3Bucket, status: ResourceStatus.NO_STATUS }),
    mkRecoveryResource({ name: 'reporting-fn', type: ResourceType.lambdaFunction, status: ResourceStatus.NO_STATUS }),
  ]
}

// All resources restored (Azure). Backs the "100% restored" reading while a sibling
// app in the same region-pair group is still finishing, so the conclude button is
// disabled despite the success status.
function resourcesAzureAllSucceeded(): ViewableApplicationResource[] {
  return [
    mkRecoveryResource({ name: 'app-vm-01', type: ResourceType.azureVirtualMachine, status: ResourceStatus.SUCCEEDED }),
    mkRecoveryResource({ name: 'app-data-disk', type: ResourceType.azureDisk, status: ResourceStatus.SUCCEEDED }),
    mkRecoveryResource({ name: 'app-sql', type: ResourceType.azureSqlDatabase, status: ResourceStatus.SUCCEEDED }),
    mkRecoveryResource({ name: 'app-vnet', type: ResourceType.azureVirtualNetwork, status: ResourceStatus.SUCCEEDED }),
  ]
}

function resourcesFailbackAvailable(): ViewableApplicationResource[] {
  // NEVER_ATTEMPTED — all rows show NO_STATUS. Only supportsFailback types
  // appear (EC2 / RDS / VPC). FailbackResource implements ViewableApplicationResource
  // so the test page POJO can stand in for the table renderer.
  return [
    mkRecoveryResource({ name: 'web-app-01', type: ResourceType.ec2Instance, status: ResourceStatus.NO_STATUS }),
    mkRecoveryResource({ name: 'web-app-02', type: ResourceType.ec2Instance, status: ResourceStatus.NO_STATUS }),
    mkRecoveryResource({ name: 'db-primary', type: ResourceType.rdsDbInstance, status: ResourceStatus.NO_STATUS }),
  ]
}

function resourcesFailbackInProgress(): ViewableApplicationResource[] {
  return [
    mkRecoveryResource({ name: 'web-app-01', type: ResourceType.ec2Instance, status: ResourceStatus.SUCCEEDED }),
    mkRecoveryResource({
      name: 'web-app-02',
      type: ResourceType.ec2Instance,
      status: ResourceStatus.PROCESSING,
      progress: 0.5,
    }),
    mkRecoveryResource({ name: 'db-primary', type: ResourceType.rdsDbInstance, status: ResourceStatus.PENDING }),
  ]
}

function resourcesFailbackAllReturned(): ViewableApplicationResource[] {
  return [
    mkRecoveryResource({ name: 'web-app-01', type: ResourceType.ec2Instance, status: ResourceStatus.SUCCEEDED }),
    mkRecoveryResource({ name: 'web-app-02', type: ResourceType.ec2Instance, status: ResourceStatus.SUCCEEDED }),
    mkRecoveryResource({ name: 'db-primary', type: ResourceType.rdsDbInstance, status: ResourceStatus.SUCCEEDED }),
  ]
}

function resourcesFailbackPartialFailure(): ViewableApplicationResource[] {
  return [
    mkRecoveryResource({ name: 'web-app-01', type: ResourceType.ec2Instance, status: ResourceStatus.SUCCEEDED }),
    mkRecoveryResource({
      name: 'web-app-02',
      type: ResourceType.ec2Instance,
      status: ResourceStatus.FAILED,
      message: 'Instance type not available in source region.',
    }),
    mkRecoveryResource({ name: 'db-primary', type: ResourceType.rdsDbInstance, status: ResourceStatus.SUCCEEDED }),
  ]
}

// Operation-notice presets — these mirror what ApplicationComponent's
// getters would produce given a particular sync phase / sandbox / quarantine
// configuration. Precomputed so the test page doesn't have to re-derive them.
const FAILOVER_NOTICE: RecoveryScreenOperationNotice = {
  recoveryPointTimeString: '03:14 PM on May 8',
  showNetworkSandboxStatus: false,
  networkSandboxStatus: 'disabled',
  showSandboxEventsLink: false,
  showRansomwareRecoveryStatus: true,
  ransomwareRecoveryStatus: 'disabled',
  domainAllowlistStatus: '',
  whitelistStatus: '',
}

const FAILOVER_NOTICE_QUARANTINED: RecoveryScreenOperationNotice = {
  ...FAILOVER_NOTICE,
  ransomwareRecoveryStatus: 'enabled',
}

const FAILOVER_TEST_NOTICE_BASIC: RecoveryScreenOperationNotice = {
  recoveryPointTimeString: '03:14 PM on May 8',
  showNetworkSandboxStatus: true,
  networkSandboxStatus: 'enabled',
  showSandboxEventsLink: false,
  showRansomwareRecoveryStatus: false,
  ransomwareRecoveryStatus: 'disabled',
  domainAllowlistStatus: '',
  whitelistStatus: 'Outbound access CIDRs: 10.0.0.0/16, 192.168.1.0/24',
}

const FAILOVER_TEST_NOTICE_ADVANCED: RecoveryScreenOperationNotice = {
  recoveryPointTimeString: '03:14 PM on May 8',
  showNetworkSandboxStatus: true,
  networkSandboxStatus: 'enabled',
  showSandboxEventsLink: true,
  showRansomwareRecoveryStatus: false,
  ransomwareRecoveryStatus: 'disabled',
  domainAllowlistStatus: 'Outbound access domains: api.snapcraft.io, github.com, pkg.go.dev, pypi.org',
  whitelistStatus: 'Outbound access CIDRs: 10.0.0.0/16',
}

// ProcessingStatus is never null-checked deeply by app-resources-view —
// passing null keeps the mock minimal and matches what the real component
// passes during pre-load. The few branches that read appStatus.inProgress
// fall through cleanly when the input is null.

export function buildRecoveryScreenScenarios(): RecoveryScreenScenario[] {
  // Each scenario constructs its own mock application so syncPhase / sandbox
  // flags align with the screen state. Cheap — buildMockApplication is a
  // plain POJO factory.
  const standbyApp = (overrides?: Partial<MockApplicationOptions>) =>
    buildMockApplication({ syncPhase: SyncPhaseDto.STANDBY, ...overrides })
  const failoverApp = (overrides?: Partial<MockApplicationOptions>) =>
    buildMockApplication({ syncPhase: SyncPhaseDto.FAILOVER, ...overrides })
  const failoverTestApp = (overrides?: Partial<MockApplicationOptions>) =>
    buildMockApplication({ syncPhase: SyncPhaseDto.FAILOVER_TEST, ...overrides })
  const deletedApp = (overrides?: Partial<MockApplicationOptions>) =>
    buildMockApplication({ syncPhase: SyncPhaseDto.STANDBY, ...overrides })

  return [
    // ─── Recovery — STANDBY ────────────────────────────────────────
    {
      key: 'r1-pre-first-backup',
      label: 'Pre-first-backup (waiting)',
      description: 'No backup has succeeded yet. Resource table is empty; status bar tells the user to wait.',
      tab: 'recovery',
      group: 'STANDBY',
      syncPhase: SyncPhaseDto.STANDBY,
      application: standbyApp(),
      resources: resourcesEmpty(),
      status: {
        state: AppStatusState.INDETERMINATE,
        text: STATUS_TEXT.recovery.waitingForFirstBackup,
        issues: [],
      },
      operationNotice: null,
      failbackOperationNoticeText: null,
      tabBarFlags: tabBarStandby({ stagingSuccess: false }),
    },
    {
      key: 'r2-initial-restore-in-progress',
      label: 'Performing initial restore (PENDING + Confirming)',
      description:
        'First restore in flight. Rows include a PENDING-with-confirming-substatus to show the post-job-lag bridge.',
      tab: 'recovery',
      group: 'STANDBY',
      syncPhase: SyncPhaseDto.STANDBY,
      application: standbyApp(),
      resources: resourcesAllPending(),
      status: {
        state: AppStatusState.INDETERMINATE,
        text: STATUS_TEXT.recovery.performingInitialRestore,
        ratioText: `0% restored (0/5)${RP_AT_3_14_MAY_8}.`,
        progressCount: 0,
        progressTotal: 5,
        issues: [],
      },
      operationNotice: null,
      failbackOperationNoticeText: null,
      // Initial restore is automatic; with appGroupRestore the standby buttons
      // stay enabled (just greyed by !lastStagingSuccess until staging lands).
      tabBarFlags: tabBarStandby({ stagingSuccess: false }),
    },
    {
      key: 'r3-steady-state-success',
      label: 'Steady state (with pilot light + orphaned target)',
      description:
        'Fully restored standby. Includes a pilot-light row (source visible, target blank) and an orphaned-target row.',
      tab: 'recovery',
      group: 'STANDBY',
      syncPhase: SyncPhaseDto.STANDBY,
      application: standbyApp(),
      resources: resourcesSteadyState(),
      status: {
        state: AppStatusState.SUCCESS,
        text: STATUS_TEXT.recovery.successfullyRestored('1 hour ago'),
        // The orphaned-target row stays NO_STATUS (not a success), so 5 of 6 count.
        ratioText: `83% restored (5/6)${RP_AT_3_14_MAY_8}.`,
        issues: [],
      },
      operationNotice: null,
      failbackOperationNoticeText: null,
      tabBarFlags: tabBarStandby(),
    },
    {
      key: 'r4-mixed-failures',
      label: 'Mixed failures (NOT_PROCESSED clickable)',
      description: 'One FAILED parent + two NOT_PROCESSED rows the user can click through to the upstream failure.',
      tab: 'recovery',
      group: 'STANDBY',
      syncPhase: SyncPhaseDto.STANDBY,
      application: standbyApp(),
      resources: resourcesMixedFailures(),
      status: {
        state: AppStatusState.ERROR,
        text: STATUS_TEXT.recovery.failedToRestoreCount(1, '1 hour ago'),
        ratioText: `40% restored (2/5)${RP_AT_3_14_MAY_8}.`,
        failedLink: { ...STATUS_TEXT.recovery.failedLinkRestore('1 hour ago'), count: 1 },
        issues: [],
      },
      operationNotice: null,
      failbackOperationNoticeText: null,
      tabBarFlags: tabBarStandby(),
    },
    {
      key: 'r5-blocked-by-dependency',
      label: 'Blocked by dependency (UNSATISFIED_DEPENDENCY)',
      description: 'Transient blocked state — two rows waiting on db-primary, db-primary itself still PROCESSING.',
      tab: 'recovery',
      group: 'STANDBY',
      syncPhase: SyncPhaseDto.STANDBY,
      application: standbyApp(),
      resources: resourcesBlocked(),
      status: {
        state: AppStatusState.INDETERMINATE,
        text: STATUS_TEXT.recovery.restoringBackup('5 minutes ago'),
        ratioText: `25% restored (1/4)${RP_AT_3_14_MAY_8}.`,
        progressCount: 1,
        progressTotal: 4,
        issues: [],
      },
      operationNotice: null,
      failbackOperationNoticeText: null,
      tabBarFlags: tabBarStandby(),
    },
    {
      key: 'r6-sibling-blocked',
      label: 'Sibling failover blocking standby restore',
      description:
        'Standby restore is queued because a sibling app sharing the region pair is in FAILOVER. Table is empty.',
      tab: 'recovery',
      group: 'STANDBY',
      syncPhase: SyncPhaseDto.STANDBY,
      application: standbyApp(),
      resources: resourcesEmpty(),
      status: {
        state: AppStatusState.INDETERMINATE,
        text: STATUS_TEXT.recovery.standbyRestoreBlocked('us-east-1', 'us-west-2'),
        issues: [],
      },
      operationNotice: null,
      failbackOperationNoticeText: null,
      tabBarFlags: tabBarStandby(),
    },
    {
      key: 'r7-eks-needs-attention',
      label: 'EKS parent with failed child (needs-attention rollup)',
      description:
        'EKS cluster + 4 pods, one FAILED, one still PROCESSING. The PROCESSING child keeps the overall status "Restoring..." while the FAILED child drives the parent needs-attention pill.',
      tab: 'recovery',
      group: 'STANDBY',
      syncPhase: SyncPhaseDto.STANDBY,
      application: standbyApp(),
      resources: resourcesEksWithFailedChild(),
      // While any child is in progress, production keeps the status INDETERMINATE
      // (statusText.ts: inProgressCount > 0 wins over the failed count). The
      // needs-attention pill is computed independently by app-resources-view.
      status: {
        state: AppStatusState.INDETERMINATE,
        text: STATUS_TEXT.recovery.restoringBackup('1 hour ago'),
        ratioText: `66% restored (4/6)${RP_AT_3_14_MAY_8}.`,
        progressCount: 4,
        progressTotal: 6,
        issues: [],
      },
      operationNotice: null,
      failbackOperationNoticeText: null,
      tabBarFlags: tabBarStandby(),
    },

    // ─── Recovery — FAILOVER ───────────────────────────────────────
    {
      key: 'r8-failover-in-progress',
      label: 'Restoring for recovery (AWS)',
      description:
        'AWS active recovery, mid-restore. CONCLUDE RECOVERY stays rendered but disabled with the restore-in-progress tooltip.',
      tab: 'recovery',
      group: 'FAILOVER',
      syncPhase: SyncPhaseDto.FAILOVER,
      application: failoverApp(),
      resources: resourcesAllPending(),
      status: {
        state: AppStatusState.INDETERMINATE,
        text: STATUS_TEXT.recovery.restoreInProgress(false),
        ratioText: `0% restored (0/5)${RP_AT_3_14_MAY_8}.`,
        progressCount: 0,
        progressTotal: 5,
        issues: [],
      },
      operationNotice: FAILOVER_NOTICE,
      failbackOperationNoticeText: null,
      // FAILOVER restore mid-flight: CONCLUDE RECOVERY stays rendered but disabled
      // with the restore-in-progress tooltip.
      tabBarFlags: tabBarFailover({
        hasFailbackResources: false,
        disabledActionReason: ACTION_DISABLED_RESTORE_IN_PROGRESS,
      }),
    },
    {
      key: 'r9-failover-complete',
      label: 'All resources restored (FAILBACK eligible)',
      description: 'Active recovery complete. In production this state lights up the FAILBACK button.',
      tab: 'recovery',
      group: 'FAILOVER',
      syncPhase: SyncPhaseDto.FAILOVER,
      application: failoverApp(),
      resources: resourcesSteadyState(),
      status: {
        state: AppStatusState.SUCCESS,
        text: STATUS_TEXT.recovery.allResourcesRestored('15 minutes ago', false),
        ratioText: `83% restored (5/6)${RP_AT_3_14_MAY_8}.`,
        issues: [],
      },
      operationNotice: FAILOVER_NOTICE,
      failbackOperationNoticeText: null,
      tabBarFlags: tabBarFailover(),
    },
    {
      key: 'r10-failover-quarantined',
      label: 'Recovery with quarantine mode ON',
      description: 'Active recovery with Quarantine Mode enabled. Notice shows quarantine state inline.',
      tab: 'recovery',
      group: 'FAILOVER',
      syncPhase: SyncPhaseDto.FAILOVER,
      application: failoverApp(),
      resources: resourcesMixedFailures(),
      status: {
        state: AppStatusState.ERROR,
        text: STATUS_TEXT.recovery.failedToRestoreCount(1, '15 minutes ago'),
        ratioText: `40% restored (2/5)${RP_AT_3_14_MAY_8}.`,
        failedLink: { ...STATUS_TEXT.recovery.failedLinkRestore('15 minutes ago'), count: 1 },
        issues: [],
      },
      operationNotice: FAILOVER_NOTICE_QUARANTINED,
      failbackOperationNoticeText: null,
      tabBarFlags: tabBarFailover({ quarantined: true }),
    },
    {
      key: 'r10b-failover-azure-region-pair',
      label: 'Restoring for recovery — region-pair wait (Azure)',
      description:
        "Azure recovery where this app's resources are all restored but a sibling app in the same region-pair group is still going. CONCLUDE RECOVERY + FAILBACK stay rendered but disabled with the region-pair tooltip.",
      tab: 'recovery',
      group: 'FAILOVER',
      syncPhase: SyncPhaseDto.FAILOVER,
      application: failoverApp({ type: ApplicationType.AZURE }),
      resources: resourcesAzureAllSucceeded(),
      status: {
        state: AppStatusState.SUCCESS,
        text: STATUS_TEXT.recovery.allResourcesRestored('15 minutes ago', false),
        ratioText: `100% restored (359/359)${RP_AT_3_14_MAY_8}.`,
        issues: [],
      },
      operationNotice: FAILOVER_NOTICE,
      failbackOperationNoticeText: null,
      tabBarFlags: tabBarFailover({ disabledActionReason: ACTION_DISABLED_AZURE_REGION_PAIR }),
    },

    // ─── Recovery — FAILOVER_TEST ──────────────────────────────────
    {
      key: 'r11-test-in-progress-advanced',
      label: 'Test recovery in progress (advanced sandbox)',
      description: 'Test cycle mid-restore. Notice shows sandbox ON with the "See most recent events" link visible.',
      tab: 'recovery',
      group: 'FAILOVER_TEST',
      syncPhase: SyncPhaseDto.FAILOVER_TEST,
      application: failoverTestApp({ networkSandboxEnabled: true, advancedNetworkSandbox: true }),
      resources: resourcesAllPending(),
      status: {
        state: AppStatusState.INDETERMINATE,
        text: STATUS_TEXT.recovery.restoreInProgress(true),
        ratioText: `0% restored (0/5)${RP_AT_3_14_MAY_8}.`,
        progressCount: 0,
        progressTotal: 5,
        issues: [],
      },
      operationNotice: FAILOVER_TEST_NOTICE_ADVANCED,
      failbackOperationNoticeText: null,
      tabBarFlags: tabBarFailoverTest({ sandboxEnabled: true }),
    },
    {
      key: 'r12-test-complete-basic',
      label: 'Test recovery complete (basic sandbox)',
      description: 'Test cycle finished. Basic sandbox shows the CIDR allowlist row but no domains list.',
      tab: 'recovery',
      group: 'FAILOVER_TEST',
      syncPhase: SyncPhaseDto.FAILOVER_TEST,
      application: failoverTestApp({ networkSandboxEnabled: true }),
      resources: resourcesSteadyState(),
      status: {
        state: AppStatusState.SUCCESS,
        text: STATUS_TEXT.recovery.allResourcesRestored('15 minutes ago', true),
        ratioText: `83% restored (5/6)${RP_AT_3_14_MAY_8}.`,
        issues: [],
      },
      operationNotice: FAILOVER_TEST_NOTICE_BASIC,
      failbackOperationNoticeText: null,
      tabBarFlags: tabBarFailoverTest({ sandboxEnabled: true }),
    },
    {
      key: 'r12b-test-in-progress-aws',
      label: 'Test recovery in progress (AWS)',
      description:
        'AWS test cycle mid-restore. CONCLUDE TEST stays enabled (shows CANCEL TEST early-stop) — the conclude dialog routes cancel-vs-conclude per app. Contrast with the Azure region-pair case.',
      tab: 'recovery',
      group: 'FAILOVER_TEST',
      syncPhase: SyncPhaseDto.FAILOVER_TEST,
      application: failoverTestApp({ networkSandboxEnabled: true }),
      resources: resourcesAllPending(),
      status: {
        state: AppStatusState.INDETERMINATE,
        text: STATUS_TEXT.recovery.restoreInProgress(true),
        ratioText: `0% restored (0/5)${RP_AT_3_14_MAY_8}.`,
        progressCount: 0,
        progressTotal: 5,
        issues: [],
      },
      operationNotice: FAILOVER_TEST_NOTICE_BASIC,
      failbackOperationNoticeText: null,
      tabBarFlags: tabBarFailoverTest({ sandboxEnabled: true, isEarlyStop: true }),
    },
    {
      key: 'r12c-test-azure-region-pair',
      label: 'Test recovery — region-pair wait (Azure)',
      description:
        'The reported bug: Azure test recovery reads 100% restored while a sibling app in the same region-pair group is still restoring. CONCLUDE TEST + SANDBOX SETTINGS stay rendered but disabled with the region-pair tooltip instead of vanishing.',
      tab: 'recovery',
      group: 'FAILOVER_TEST',
      syncPhase: SyncPhaseDto.FAILOVER_TEST,
      application: failoverTestApp({ type: ApplicationType.AZURE, networkSandboxEnabled: true }),
      resources: resourcesAzureAllSucceeded(),
      status: {
        state: AppStatusState.SUCCESS,
        text: STATUS_TEXT.recovery.allResourcesRestored('43 minutes ago', true),
        ratioText: `100% restored (359/359)${RP_AT_3_14_MAY_8}.`,
        issues: [],
      },
      operationNotice: FAILOVER_TEST_NOTICE_BASIC,
      failbackOperationNoticeText: null,
      tabBarFlags: tabBarFailoverTest({
        sandboxEnabled: true,
        disabledActionReason: ACTION_DISABLED_AZURE_REGION_PAIR,
      }),
    },

    // ─── Recovery — Cleanup (deleted app) ──────────────────────────
    {
      key: 'r13-deletion-cleanup',
      label: 'Cleaning up recovery environment',
      description: 'App marked deleted; cleanup in progress. Mirrors the override that suppresses phase-based status.',
      tab: 'recovery',
      group: 'CLEANUP',
      syncPhase: SyncPhaseDto.STANDBY,
      application: deletedApp(),
      resources: resourcesAllPending(),
      status: {
        state: AppStatusState.INDETERMINATE,
        text: STATUS_TEXT.recovery.cleaningUp,
        issues: [],
      },
      operationNotice: null,
      failbackOperationNoticeText: null,
      // Deleted app hides TEST / LAUNCH RECOVERY entirely, so there's nothing to disable.
      tabBarFlags: tabBarStandby({ appIsDeleted: true }),
    },
    {
      key: 'r14-concluding-to-standby',
      label: 'Concluding recovery — winding back to standby',
      description:
        'A recovery was just concluded; the app is restoring back to standby. TEST / LAUNCH RECOVERY stay rendered but disabled with the wind-down tooltip until the restore completes.',
      tab: 'recovery',
      group: 'STANDBY',
      syncPhase: SyncPhaseDto.STANDBY,
      application: standbyApp(),
      resources: resourcesAllPending(),
      status: {
        state: AppStatusState.INDETERMINATE,
        text: STATUS_TEXT.recovery.restoringBackup('5 minutes ago'),
        ratioText: `0% restored (0/5)${RP_AT_3_14_MAY_8}.`,
        progressCount: 0,
        progressTotal: 5,
        issues: [],
      },
      operationNotice: null,
      failbackOperationNoticeText: null,
      // isConcludingToStandby — buttons disabled with the conclude-to-standby tooltip.
      tabBarFlags: tabBarStandby({ disabledActionReason: ACTION_DISABLED_CONCLUDING_TO_STANDBY }),
    },

    // ─── Recovery — STANDBY (additional status variants) ───────────
    {
      key: 'r15-first-backup-in-progress',
      label: 'Initial backup in progress (restore queued)',
      description:
        'No backup has ever succeeded and one is actively running. Restore to standby will start automatically once it lands.',
      tab: 'recovery',
      group: 'STANDBY',
      syncPhase: SyncPhaseDto.STANDBY,
      application: standbyApp(),
      resources: resourcesEmpty(),
      status: {
        state: AppStatusState.INDETERMINATE,
        text: STATUS_TEXT.recovery.firstBackupInProgress,
        issues: [],
      },
      operationNotice: null,
      failbackOperationNoticeText: null,
      tabBarFlags: tabBarStandby({ stagingSuccess: false }),
    },
    {
      key: 'r16-waiting-initial-restore',
      label: 'Waiting to perform initial restore',
      description:
        'First backup has succeeded; resources are configured but the initial standby restore has not started yet (all NO_STATUS).',
      tab: 'recovery',
      group: 'STANDBY',
      syncPhase: SyncPhaseDto.STANDBY,
      application: standbyApp(),
      resources: resourcesAllNoStatus(),
      status: {
        state: AppStatusState.INDETERMINATE,
        text: STATUS_TEXT.recovery.waitingForInitialRestore,
        issues: [],
      },
      operationNotice: null,
      failbackOperationNoticeText: null,
      tabBarFlags: tabBarStandby(),
    },
    {
      key: 'r17-restored-with-issues',
      label: 'Successfully restored — with open issues',
      description:
        'Standby restore complete but issues are open against the recovery point. State is WARNING; the issues link sits next to the headline.',
      tab: 'recovery',
      group: 'STANDBY',
      syncPhase: SyncPhaseDto.STANDBY,
      application: standbyApp(),
      resources: resourcesSteadyState(),
      status: {
        state: AppStatusState.WARNING,
        text: STATUS_TEXT.recovery.successfullyRestored('1 hour ago'),
        ratioText: `83% restored (5/6)${RP_AT_3_14_MAY_8}.`,
        issues: [{} as any],
      },
      operationNotice: null,
      failbackOperationNoticeText: null,
      tabBarFlags: tabBarStandby(),
    },
    {
      key: 'r18-initial-restore-failed',
      label: 'Initial restore to standby failed',
      description:
        'First-ever standby restore failed at the job level (no per-resource FAILED rows). Distinct "Initial restore..." copy.',
      tab: 'recovery',
      group: 'STANDBY',
      syncPhase: SyncPhaseDto.STANDBY,
      application: standbyApp(),
      resources: resourcesPartialNoFailure(),
      status: {
        state: AppStatusState.ERROR,
        text: STATUS_TEXT.recovery.initialRestoreFailed,
        ratioText: `25% restored (1/4)${RP_AT_3_14_MAY_8}.`,
        issues: [],
      },
      operationNotice: null,
      failbackOperationNoticeText: null,
      tabBarFlags: tabBarStandby(),
    },
    {
      key: 'r19-failed-to-apply-backup',
      label: 'Subsequent restore to standby failed',
      description:
        'A prior standby restore succeeded; the latest one failed at the job level. Uses the "Failed to restore backup..." copy rather than the initial-restore copy.',
      tab: 'recovery',
      group: 'STANDBY',
      syncPhase: SyncPhaseDto.STANDBY,
      application: standbyApp(),
      resources: resourcesPartialNoFailure(),
      status: {
        state: AppStatusState.ERROR,
        text: STATUS_TEXT.recovery.failedToApplyBackup('1 hour ago'),
        ratioText: `25% restored (1/4)${RP_AT_3_14_MAY_8}.`,
        issues: [],
      },
      operationNotice: null,
      failbackOperationNoticeText: null,
      tabBarFlags: tabBarStandby(),
    },
    {
      key: 'r20-standby-refresh-disabled',
      label: 'Standby refresh — actions disabled (no appGroupRestore)',
      description:
        'A STANDBY refresh restore is in flight on an account WITHOUT the appGroupRestore entitlement, so the action buttons disable with the standby-refresh tooltip. (With the entitlement they stay enabled — see r2.)',
      tab: 'recovery',
      group: 'STANDBY',
      syncPhase: SyncPhaseDto.STANDBY,
      application: standbyApp(),
      resources: resourcesAllPending(),
      status: {
        state: AppStatusState.INDETERMINATE,
        text: STATUS_TEXT.recovery.restoringBackup('5 minutes ago'),
        ratioText: `0% restored (0/5)${RP_AT_3_14_MAY_8}.`,
        progressCount: 0,
        progressTotal: 5,
        issues: [],
      },
      operationNotice: null,
      failbackOperationNoticeText: null,
      tabBarFlags: tabBarStandby({ disabledActionReason: ACTION_DISABLED_STANDBY_REFRESH }),
    },

    // ─── Recovery — FAILOVER (additional status variants) ──────────
    {
      key: 'r21-failover-preparing',
      label: 'Preparing to restore for recovery',
      description: 'Recovery launched but no resource has begun restoring yet (NEVER_ATTEMPTED). No ratio shown.',
      tab: 'recovery',
      group: 'FAILOVER',
      syncPhase: SyncPhaseDto.FAILOVER,
      application: failoverApp(),
      resources: resourcesEmpty(),
      status: {
        state: AppStatusState.INDETERMINATE,
        text: STATUS_TEXT.recovery.preparingRestore(false),
        issues: [],
      },
      operationNotice: FAILOVER_NOTICE,
      failbackOperationNoticeText: null,
      tabBarFlags: tabBarFailover({ hasFailbackResources: false }),
    },
    {
      key: 'r22-failover-restore-failed',
      label: 'Restore for recovery failed (job-level)',
      description:
        'Recovery restore failed at the job level with no per-resource FAILED rows. Uses the "Restore for recovery failed." copy.',
      tab: 'recovery',
      group: 'FAILOVER',
      syncPhase: SyncPhaseDto.FAILOVER,
      application: failoverApp(),
      resources: resourcesPartialNoFailure(),
      status: {
        state: AppStatusState.ERROR,
        text: STATUS_TEXT.recovery.restoreFailed(false),
        ratioText: `25% restored (1/4)${RP_AT_3_14_MAY_8}.`,
        issues: [],
      },
      operationNotice: FAILOVER_NOTICE,
      failbackOperationNoticeText: null,
      tabBarFlags: tabBarFailover({ hasFailbackResources: false }),
    },

    // ─── Recovery — FAILOVER_TEST (additional status variants) ─────
    {
      key: 'r23-test-preparing',
      label: 'Preparing to restore for test recovery',
      description: 'Test recovery launched but no resource has begun restoring yet (NEVER_ATTEMPTED). No ratio shown.',
      tab: 'recovery',
      group: 'FAILOVER_TEST',
      syncPhase: SyncPhaseDto.FAILOVER_TEST,
      application: failoverTestApp({ networkSandboxEnabled: true }),
      resources: resourcesEmpty(),
      status: {
        state: AppStatusState.INDETERMINATE,
        text: STATUS_TEXT.recovery.preparingRestore(true),
        issues: [],
      },
      operationNotice: FAILOVER_TEST_NOTICE_BASIC,
      failbackOperationNoticeText: null,
      tabBarFlags: tabBarFailoverTest({ sandboxEnabled: true }),
    },
    {
      key: 'r24-test-restore-failed',
      label: 'Restore for test recovery failed (job-level)',
      description:
        'Test recovery restore failed at the job level with no per-resource FAILED rows. Uses the "Restore for test recovery failed." copy.',
      tab: 'recovery',
      group: 'FAILOVER_TEST',
      syncPhase: SyncPhaseDto.FAILOVER_TEST,
      application: failoverTestApp({ networkSandboxEnabled: true }),
      resources: resourcesPartialNoFailure(),
      status: {
        state: AppStatusState.ERROR,
        text: STATUS_TEXT.recovery.restoreFailed(true),
        ratioText: `25% restored (1/4)${RP_AT_3_14_MAY_8}.`,
        issues: [],
      },
      operationNotice: FAILOVER_TEST_NOTICE_BASIC,
      failbackOperationNoticeText: null,
      tabBarFlags: tabBarFailoverTest({ sandboxEnabled: true }),
    },

    // ─── Recovery — Cleanup (additional status variants) ───────────
    {
      key: 'r25-finalizing-deletion',
      label: 'Finalizing application deletion',
      description: 'App marked deleted and all recovery resources have been cleaned up; deletion is finalizing.',
      tab: 'recovery',
      group: 'CLEANUP',
      syncPhase: SyncPhaseDto.STANDBY,
      application: deletedApp(),
      resources: resourcesEmpty(),
      status: {
        state: AppStatusState.INDETERMINATE,
        text: STATUS_TEXT.recovery.finalizingDeletion,
        issues: [],
      },
      operationNotice: null,
      failbackOperationNoticeText: null,
      tabBarFlags: tabBarStandby({ appIsDeleted: true }),
    },
    {
      key: 'r26-cleanup-failed',
      label: 'Recovery environment cleanup failed',
      description: 'App marked deleted but cleanup hit an error (restore issues present). State is ERROR.',
      tab: 'recovery',
      group: 'CLEANUP',
      syncPhase: SyncPhaseDto.STANDBY,
      application: deletedApp(),
      resources: resourcesMixedFailures(),
      status: {
        state: AppStatusState.ERROR,
        text: STATUS_TEXT.recovery.cleanupFailed,
        issues: [{} as any],
      },
      operationNotice: null,
      failbackOperationNoticeText: null,
      tabBarFlags: tabBarStandby({ appIsDeleted: true }),
    },

    // ─── Failback ──────────────────────────────────────────────────
    {
      key: 'f1-no-supported',
      label: 'No resources support failback',
      description: 'Account is entitled, but the app has no failback-supporting resource types.',
      tab: 'failback',
      group: 'FAILBACK',
      syncPhase: SyncPhaseDto.FAILOVER,
      application: failoverApp(),
      resources: resourcesEmpty(),
      status: {
        state: AppStatusState.INFO,
        text: STATUS_TEXT.failback.noSupported,
        issues: [],
      },
      operationNotice: null,
      failbackOperationNoticeText: null,
      tabBarFlags: tabBarFailover({ hasFailbackResources: false }),
    },
    {
      key: 'f2-available',
      label: 'N resources available for failback',
      description:
        'Failback has never been attempted. All rows NO_STATUS — the FAILBACK button is enabled in production.',
      tab: 'failback',
      group: 'FAILBACK',
      syncPhase: SyncPhaseDto.FAILOVER,
      application: failoverApp(),
      resources: resourcesFailbackAvailable(),
      status: {
        state: AppStatusState.SUCCESS,
        text: STATUS_TEXT.failback.available(3),
        issues: [],
      },
      operationNotice: null,
      failbackOperationNoticeText: null,
      tabBarFlags: tabBarFailover(),
    },
    {
      key: 'f3-in-progress',
      label: 'First failback attempt in progress',
      description: 'Failback running. Status bar shows ratio; operation notice surfaces the running-state copy.',
      tab: 'failback',
      group: 'FAILBACK',
      syncPhase: SyncPhaseDto.FAILOVER,
      application: failoverApp(),
      resources: resourcesFailbackInProgress(),
      status: {
        state: AppStatusState.INDETERMINATE,
        text: STATUS_TEXT.failback.returning,
        ratioText: '33% returned (1/3).',
        progressCount: 1,
        progressTotal: 3,
        issues: [],
      },
      operationNotice: null,
      failbackOperationNoticeText: 'Failback is returning resources to the primary environment.',
      tabBarFlags: tabBarFailover({ disabledActionReason: ACTION_DISABLED_FAILBACK_IN_PROGRESS }),
    },
    {
      key: 'f4-all-returned',
      label: 'All resources returned',
      description: 'Successful failback complete. Notice surfaces the success copy.',
      tab: 'failback',
      group: 'FAILBACK',
      syncPhase: SyncPhaseDto.FAILOVER,
      application: failoverApp(),
      resources: resourcesFailbackAllReturned(),
      status: {
        state: AppStatusState.SUCCESS,
        text: STATUS_TEXT.failback.allReturned,
        ratioText: '100% returned (3/3).',
        issues: [],
      },
      operationNotice: null,
      failbackOperationNoticeText: 'Failback complete. Resources have been returned to the primary environment.',
      tabBarFlags: tabBarFailover(),
    },
    {
      key: 'f5-partial-failure',
      label: 'Partial failure — N resources failed to return',
      description:
        "The most recent failback attempt failed with one resource left un-returned (MOST_RECENT_FAILED). No completion notice shows because the last attempt didn't succeed. Clickable failed-link in the status bar filters the table.",
      tab: 'failback',
      group: 'FAILBACK',
      syncPhase: SyncPhaseDto.FAILOVER,
      application: failoverApp(),
      resources: resourcesFailbackPartialFailure(),
      status: {
        state: AppStatusState.ERROR,
        text: STATUS_TEXT.failback.failedToReturnCount(1),
        // floor(2/3 * 100) = 66, matching computeFailbackStatus's Math.floor ratio.
        ratioText: '66% returned (2/3).',
        failedLink: { ...STATUS_TEXT.failback.failedLinkReturn, count: 1 },
        issues: [],
      },
      operationNotice: null,
      failbackOperationNoticeText: null,
      tabBarFlags: tabBarFailover(),
    },
    {
      key: 'f6-retry-in-progress',
      label: 'Retrying failback (SUBSEQUENT_ATTEMPT)',
      description: 'A previous failback attempt failed; a retry is now running.',
      tab: 'failback',
      group: 'FAILBACK',
      syncPhase: SyncPhaseDto.FAILOVER,
      application: failoverApp(),
      resources: resourcesFailbackInProgress(),
      status: {
        state: AppStatusState.INDETERMINATE,
        text: STATUS_TEXT.failback.retrying,
        // resourcesFailbackInProgress has 1 SUCCEEDED of 3 → 33%, matching the table.
        ratioText: '33% returned (1/3).',
        progressCount: 1,
        progressTotal: 3,
        issues: [],
      },
      operationNotice: null,
      failbackOperationNoticeText: 'Failback is returning resources to the primary environment.',
      tabBarFlags: tabBarFailover({ disabledActionReason: ACTION_DISABLED_FAILBACK_IN_PROGRESS }),
    },
    {
      key: 'f7-job-failure',
      label: 'Terminal job failure (no resource-level failures)',
      description: 'Failback job itself failed without individual resource failures.',
      tab: 'failback',
      group: 'FAILBACK',
      syncPhase: SyncPhaseDto.FAILOVER,
      application: failoverApp(),
      resources: resourcesFailbackAvailable(),
      status: {
        state: AppStatusState.ERROR,
        text: STATUS_TEXT.failback.failed,
        // Production always emits a ratio when totalCount > 0, even on terminal failure.
        ratioText: '0% returned (0/3).',
        issues: [],
      },
      operationNotice: null,
      failbackOperationNoticeText: null,
      tabBarFlags: tabBarFailover(),
    },
  ]
}

// Resource tab enum re-export so the test page can bind to the failback tab
// type when wiring the screen preview without importing ResourcesTab itself.
export const RECOVERY_SCREEN_TABS = {
  recovery: ResourcesTab.RECOVERY_ENVIRONMENT,
  failback: ResourcesTab.FAILBACK,
} as const

// -------------------------------------------------------------------------
// Backup screen scenarios (ARP-7143 — Backup tab counterpart to Recovery)
// -------------------------------------------------------------------------
//
// Same scenario-driven snapshot pattern as the Recovery screen section, but
// scoped to the Backup tab. Differences from Recovery:
//   - No sub-tabs (Backup has no Recovery/Failback chips)
//   - No operation-status notice (Backup tab doesn't render one in production)
//   - Resource table uses StatusContext.BACKUP (pill labels: Backing Up / Ready / Failed)
//   - Status object is BackupHealthStatus (carries optional priorBackupInfo)
//
// Tab-bar action gating is identical to Recovery — the action strip is
// persistent across both tabs in production, so we reuse RecoveryScreenTabBarFlags
// + the same tabBarStandby/tabBarFailover/tabBarFailoverTest helpers.
